import { gql, useMutation, useQuery } from '@apollo/client';
import classNames from 'classnames';
import moment from 'moment';
import { Delta } from 'quill/core';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import useAppSelector from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import IconButton from '../../components/IconButton';
import LoadingIndicator from '../../components/LoadingIndicator';
import POST_CONTENT_FRAGMENT from '../../graphql/fragments/PostContent';
import type {
  PostHistoryContentQuery,
  PostHistoryContentQueryVariables,
  PostHistoryRangeQuery,
  PostHistoryRangeQueryVariables,
  UpdatePostContentMutation,
  UpdatePostContentMutationVariables,
} from '../../graphql/generated/graphqlTypes';
import arrowLeftIcon from '../../icons/arrowLeft.svg';
import UIActions from '../../ui/UIActions';
import isSize from '../../utils/responsive/isSize';
import Sizes from '../../utils/responsive/Sizes';
import useSlug from '../../utils/router/useSlug';
import { useLocation, useRouter } from '../../utils/router/v3Bridge';
import postPath from '../../utils/url/postPath';
import { PostPolicyAction, usePostPolicyCheck } from '../postPolicyCheck';
import usePostChromeVisible from '../usePostChromeVisible';
import HistoricalPost from './HistoricalPost';
import HistorySidebar from './HistorySidebar';
import styles from './PostHistoryView.module.scss';
import PostHistoryWrapper from './PostHistoryWrapper';

const HISTORY_RANGE_QUERY = gql`
  query PostHistoryRange($postId: ID!, $versionFrom: Int!, $versionTo: Int!) {
    post(id: $postId) {
      id
      ...PostContent
      ...PostHistoryWrapper_post
      history(versionFrom: $versionFrom, versionTo: $versionTo) {
        id
        snapshot
        delta
        diff
        insertedAt
        versionTo
      }
      ...postPath_post
      ...usePostPolicyCheck_post
    }
  }
  ${POST_CONTENT_FRAGMENT}
  ${PostHistoryWrapper.fragments.post}
  ${postPath.fragments.post}
  ${usePostPolicyCheck.fragments.post}
`;

const POST_CONTENT_QUERY = gql`
  query PostHistoryContent($postId: ID!) {
    post(id: $postId) {
      id
      ...PostContent
    }
  }
  ${POST_CONTENT_FRAGMENT}
`;

const UPDATE_POST_CONTENT = gql`
  mutation updatePostContent(
    $id: ID!
    $delta: Json!
    $version: Int!
    $checksum: Int!
  ) {
    post: updatePostContent(
      id: $id
      delta: $delta
      version: $version
      checksum: $checksum
    ) {
      id
    }
  }
`;

const PostHistoryView = () => {
  const postPolicyCheck = usePostPolicyCheck();
  const dispatch = useDispatch();
  const currentUser = useAppSelector((state) => state.session.user);

  const router = useRouter();
  const { pathname } = useLocation();
  const [postId] = useSlug('postSlug');
  const { versionFrom, versionTo } = useParams() as {
    postId: string;
    versionFrom: string;
    versionTo: string;
  };

  const { data } = useQuery<
    PostHistoryRangeQuery,
    PostHistoryRangeQueryVariables
  >(HISTORY_RANGE_QUERY, {
    skip: !versionFrom,
    fetchPolicy: 'network-only',
    variables: {
      postId,
      versionFrom: parseInt(versionFrom, 10),
      versionTo: parseInt(versionTo, 10),
    },
  });

  const { refetch: getPostContent } = useQuery<
    PostHistoryContentQuery,
    PostHistoryContentQueryVariables
  >(POST_CONTENT_QUERY, {
    skip: true,
    fetchPolicy: 'network-only',
    variables: { postId },
  });

  const [updatePostContent] = useMutation<
    UpdatePostContentMutation,
    UpdatePostContentMutationVariables
  >(UPDATE_POST_CONTENT);

  useEffect(() => {
    if (!isSize(Sizes.SM)) {
      dispatch(
        UIActions.openRightPanel(HistorySidebar, { postId, tab: 'versions' }),
      );
    }
    return () => {
      dispatch(UIActions.closeRightPanel());
    };
  }, [dispatch, postId]);

  const [isRestoring, setIsRestoring] = useState(false);

  const { post } = data ?? {};
  const history = post?.history;

  const content = useMemo(
    () =>
      history
        ? new Delta(history.snapshot).compose(new Delta(history.diff))
        : null,
    [history],
  );

  const isChromeVisible = usePostChromeVisible();

  const canRestore =
    post &&
    history &&
    history.versionTo !== post.version &&
    postPolicyCheck(PostPolicyAction.EditPost, post, currentUser);

  const restoreButton = canRestore ? (
    <Button
      size="small"
      variant="secondary"
      disabled={isRestoring}
      onClick={() => {
        setIsRestoring(true);
        getPostContent().then(({ data: { post: currentPost } }) => {
          const { snapshot, delta } = history;

          const currentDelta = new Delta(currentPost.content);
          const targetDelta = new Delta(snapshot).compose(new Delta(delta));

          const diff = currentDelta.diff(targetDelta);

          return updatePostContent({
            variables: {
              id: postId,
              delta: JSON.stringify(diff),
              version: currentPost.version,
              checksum: currentDelta.length(),
            },
          }).then(() => {
            setIsRestoring(false);
            router.push(postPath(post));
            dispatch(
              UIActions.pushToast({
                level: 'success',
                message: 'Restored your post!',
              }),
            );
          });
        });
      }}
    >
      {isRestoring ? <LoadingIndicator delay={0} /> : 'Restore this version'}
    </Button>
  ) : null;

  return (
    <PostHistoryWrapper>
      <div
        className={classNames(styles.barContainer, {
          [styles.hide]: !isChromeVisible,
        })}
      >
        <div className={styles.bar}>
          <div className={styles.content}>
            <IconButton
              onClick={() => {
                const newPath = pathname.split('/histories').shift();
                if (newPath) {
                  router.push(newPath);
                }
              }}
            >
              {arrowLeftIcon}
            </IconButton>
            {history && (
              // moment('') is invalid
              <div className={styles.versionMeta}>
                Version edited at{' '}
                {moment(history.insertedAt || undefined).format('lll')}
              </div>
            )}
          </div>
          <div className={styles.actions}>{restoreButton}</div>
        </div>
      </div>
      {content && <HistoricalPost content={content} />}
    </PostHistoryWrapper>
  );
};

export default PostHistoryView;
